import React, { useState, ChangeEvent, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGoogle,
  faFacebook,
  faTwitter,
  faLinkedin,
} from '@fortawesome/free-brands-svg-icons';

import { LoginDTO } from '../../dto/LoginDTO';
import { AuthResponseDTO } from '../../dto/AuthResponseDTO';
import { apiClient } from '../../config/ApiClient';
import { showSuccess, showError } from '../../components/Alert';

import '../../styles/LoginForm.css';

const LoginForm: React.FC = () => {
  const [userName, setUserName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Front-end validation
    if (!userName.trim() || !password) {
      showError('Please enter both username/email and password');
      setIsLoading(false);
      return;
    }

    const credentials: LoginDTO = { userName, password };

    try {
      const { data } = await apiClient.post<AuthResponseDTO>(
        '/auth/login',
        credentials
      );
      const { accessToken } = data;

      // 1. Store raw token
      localStorage.setItem('token', accessToken);

      // 2. Manually decode JWT payload to extract "sub"
      const parts = accessToken.split('.');
      if (parts.length === 3) {
        const payloadBase64 = parts[1];
        const decodedJson = atob(payloadBase64);
        const { sub } = JSON.parse(decodedJson) as { sub: string };
        localStorage.removeItem('userName');
        localStorage.setItem('userName', sub);
      } else {
        showError('Received malformed token');
      }

      showSuccess('Login successful!');
      navigate('/');
    } catch (err: any) {
      if (err.response) {
        const status = err.response.status;
        if (status === 400 || status === 401) {
          showError('Incorrect username or password');
        } else {
          const serverMsg =
            typeof err.response.data === 'string'
              ? err.response.data
              : err.response.data?.message || JSON.stringify(err.response.data);
          showError(serverMsg || 'Login failed');
        }
      } else {
        showError(err.message || 'Network error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (platform: string) => {
    showError(`Login with ${platform} is not implemented yet.`);
  };

  return (
    <div className="login-form-wrapper">
      <form onSubmit={handleLogin} noValidate>
        <h2 className="form-title">Login Now</h2>

        <div className="input-group mt-3">
          <label htmlFor="userName" className="input-group-text">
            <i className="bi bi-person-fill"></i>
          </label>
          <input
            id="userName"
            type="text"
            className="form-control input-box"
            maxLength={40}
            placeholder="Username or email"
            value={userName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setUserName(e.target.value)
            }
            required
            disabled={isLoading}
          />
        </div>

        <div className="input-group mt-3">
          <label htmlFor="password" className="input-group-text">
            <i className="bi bi-key-fill"></i>
          </label>
          <input
            id="password"
            type="password"
            className="form-control input-box"
            maxLength={40}
            placeholder="Password"
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            required
            disabled={isLoading}
          />
        </div>

        <div className="my-4 text-center">
          <button
            type="submit"
            className="btn btn-lg btn-outline-dark"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              'Log In'
            )}
          </button>
        </div>

        <Link to="/forgot-password" className="forgot-password">
          Forgot Password?
        </Link>

        {/*
        <div className="or-login-text">Or you can login with</div>
        <div className="social-login-buttons">
          {[
            { icon: faGoogle, name: 'Google' },
            { icon: faFacebook, name: 'Facebook' },
            { icon: faTwitter, name: 'Twitter' },
            { icon: faLinkedin, name: 'LinkedIn' },
          ].map(({ icon, name }) => (
            <div
              key={name}
              className={`social-login-button ${name.toLowerCase()}`}
              onClick={() => handleSocialLogin(name)}
            >
              <FontAwesomeIcon icon={icon} />
            </div>
          ))}
        </div>
        */}

        <div className="signup-text">
          Don't have an account?{' '}
          <Link to="/signup" className="link">
            Sign Up
          </Link>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
